import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CUSTOM_WAGE_FREQUENCY_LABELS,
  PAY_WAGE_FREQUENCIES,
  isCustomWageFrequency,
  type PayWageFrequency,
} from "../utils/custom-wage";

interface CustomWageFieldsProps {
  idPrefix: string;
  amount: string;
  frequency: PayWageFrequency;
  onAmountChange: (amount: string) => void;
  onFrequencyChange: (frequency: PayWageFrequency) => void;
  error?: string;
  disabled?: boolean;
  // For callers whose surrounding group already names the field.
  isLabelHidden?: boolean;
}

/** Dollar amount plus pay frequency, shared by the provider and org dialogs. */
export function CustomWageFields({
  idPrefix,
  amount,
  frequency,
  onAmountChange,
  onFrequencyChange,
  error,
  disabled,
  isLabelHidden,
}: CustomWageFieldsProps) {
  const amountId = `${idPrefix}-amount`;
  const errorId = `${idPrefix}-error`;

  return (
    <div className="space-y-2">
      <Label
        htmlFor={amountId}
        className={isLabelHidden ? "sr-only" : undefined}
      >
        Wage
      </Label>
      <div className="flex items-start gap-2">
        <div className="relative min-w-0 flex-1">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500"
          >
            $
          </span>
          <Input
            id={amountId}
            inputMode="decimal"
            placeholder="0.00"
            className="pl-7"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
          />
        </div>
        <Select
          value={frequency}
          onValueChange={(value) => {
            if (isCustomWageFrequency(value)) {
              onFrequencyChange(value);
            }
          }}
          disabled={disabled}
        >
          <SelectTrigger className="w-[130px] shrink-0" aria-label="Frequency">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAY_WAGE_FREQUENCIES.map((option) => (
              <SelectItem key={option} value={option}>
                {CUSTOM_WAGE_FREQUENCY_LABELS[option]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error && (
        <p id={errorId} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
